import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { IconXCircle, IconExternalLink, IconFileText } from '@/components/icons/Icons'

/*
  Visor de una cotizacion sin salir del proyecto.

  Antes, tocar una cotizacion vinculada navegaba al modulo de cotizaciones y
  habia que volver: se perdia la pestana, el filtro y el scroll de la ficha, y
  al regresar se recargaba todo. Casi siempre uno solo quiere mirar el
  documento, no editarlo, asi que se abre aqui y el enlace al modulo completo
  sigue estando para cuando si hace falta.

  El HTML es el mismo que arma el PDF (`/api/pdf/preview/cotizacion/:id`), asi
  que lo que se ve aca es exactamente lo que recibio el cliente.
*/
export default function CotizacionVisorModal({
  cotizacionId,
  numero,
  onClose,
}: {
  cotizacionId: number
  numero?: string | number
  onClose: () => void
}) {
  const [html, setHtml] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const cerrarConEsc = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', cerrarConEsc)
    return () => window.removeEventListener('keydown', cerrarConEsc)
  }, [onClose])

  useEffect(() => {
    let vivo = true
    const token = localStorage.getItem('auth_token')
    const base = import.meta.env.VITE_API_URL || ''
    fetch(`${base}/api/pdf/preview/cotizacion/${cotizacionId}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    })
      .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.text() })
      .then(t => { if (vivo) setHtml(t) })
      .catch(() => { if (vivo) setError('No se pudo cargar la vista previa de la cotizacion.') })
    return () => { vivo = false }
  }, [cotizacionId])

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-content cotizacion-visor"
        onClick={e => e.stopPropagation()}
        role="dialog"
        aria-label={`Cotizacion ${numero ?? cotizacionId}`}
      >
        <div className="modal-header">
          <h2 style={{ display: 'flex', alignItems: 'center', gap: 8, margin: 0 }}>
            <IconFileText size={18} /> Cotizacion {numero != null ? `#${numero}` : ''}
          </h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginLeft: 'auto' }}>
            <Link to={`/cotizaciones/${cotizacionId}`} className="btn btn-secondary btn-sm" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <IconExternalLink size={14} /> Abrir en Cotizaciones
            </Link>
            <button className="btn btn-icon btn-sm btn-secondary" onClick={onClose} title="Cerrar">
              <IconXCircle size={16} />
            </button>
          </div>
        </div>

        <div className="cotizacion-visor__cuerpo">
          {error ? (
            <div className="empty-state"><p>{error}</p></div>
          ) : html == null ? (
            <div className="empty-state"><p>Cargando la cotizacion...</p></div>
          ) : (
            // `sandbox` sin `allow-scripts`: el documento es solo para mirarlo.
            <iframe
              title={`Cotizacion ${numero ?? cotizacionId}`}
              srcDoc={html}
              sandbox=""
              className="cotizacion-visor__marco"
            />
          )}
        </div>
      </div>
    </div>
  )
}
